import type { ReputationDto } from '@/api/types'
import { Link } from 'react-router-dom'
import { DiplomacyIcon } from '@/components/game/GameIcons'
import { ReputationIcon, RANK_NAMES } from '@/components/game/ReputationIcon'
import { compassPosition } from '@/lib/reputation'

/** Axe Légal↔Hors-la-loi · Marchand↔Guerrier déduit de la faction (le quadrant). */
const AXIS_LABELS: Record<string, string> = {
  guilde: 'Légal · Marchand',
  marine: 'Légal · Guerrier',
  corsaire: 'Hors-la-loi · Marchand',
  pirate: 'Hors-la-loi · Guerrier',
}

/**
 * Shows the player's emergent faction: pavillon + title + a 2-axis plot
 * (loyalty × method) marking where their deeds have placed them. Pure
 * presentational — fed by /api/me's reputation block.
 */
export function ReputationCard({ reputation }: { reputation: ReputationDto }) {
  const isNeutral = reputation.faction === 'neutral'
  const prefix = RANK_NAMES[reputation.rank - 1]
  const axisLabel = AXIS_LABELS[reputation.faction]

  return (
    <section className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex items-center gap-4 sm:gap-5 min-w-0 sm:flex-1">
          {/* faction emblem — the rank medallion: glyph + frame escalate with notoriety */}
          <div className="relative grid place-items-center w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-xl bg-ocean-900/40 border border-sand-500/20 p-1">
            <ReputationIcon
              faction={reputation.faction}
              rank={reputation.rank}
              size={72}
              className="w-full h-full"
              title={reputation.title}
            />
          </div>

          <div className="min-w-0">
            <h2 className="font-display text-xl sm:text-2xl text-sand-100">{reputation.factionLabel}</h2>
            {!isNeutral && axisLabel && (
              <p className="text-[11px] uppercase tracking-[0.2em] text-ocean-300 mt-1">{axisLabel}</p>
            )}
            {!isNeutral && prefix && (
              <p className="mt-1.5 flex items-center gap-2 text-sm">
                <span className="text-base leading-none tracking-[0.15em] text-sand-300" aria-hidden="true">
                  {'★'.repeat(reputation.rank)}
                </span>
                <span className="font-medium text-sand-100">
                  {prefix}
                  <span className="font-normal text-ocean-300"> · rang {reputation.rank}</span>
                </span>
              </p>
            )}
            <p className="text-sm text-ocean-200 mt-1.5 max-w-md">{reputation.description}</p>
          </div>
        </div>

        <div className="self-center sm:self-auto shrink-0">
          <ReputationCompass reputation={reputation} />
        </div>
      </div>
      <Link
        to="/negociations"
        className="mt-4 inline-flex items-center gap-2 text-sm text-sand-300 hover:text-sand-100 transition-colors"
      >
        <DiplomacyIcon className="w-4 h-4" />
        La table des négociations
        <span aria-hidden="true">→</span>
      </Link>
    </section>
  )
}

/**
 * The 2-axis allegiance plot (loyalty × method) on its own — reused both inside
 * the dashboard ReputationCard and in the captain's merged livret-de-bord banner.
 */
export function ReputationCompass({ reputation }: { reputation: ReputationDto }) {
  // Map the loyalty/method axes → [0, 100]% for plotting, clamped so a value
  // can never render the dot outside the box (see lib/reputation.ts).
  const { x, y } = compassPosition(reputation.loyalty, reputation.method)

  return (
    <div className="relative w-36 h-36 shrink-0 rounded-xl bg-ocean-900/55 border border-sand-500/20 overflow-hidden">
      <div className="absolute inset-x-3 top-1/2 h-px bg-sand-500/20" />
      <div className="absolute inset-y-3 left-1/2 w-px bg-sand-500/20" />
      <span className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider text-ocean-400">légal</span>
      <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider text-ocean-400">hors-la-loi</span>
      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-wider text-ocean-400 [writing-mode:vertical-lr] rotate-180">marchand</span>
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-wider text-ocean-400 [writing-mode:vertical-lr]">guerrier</span>
      <span
        className="absolute w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sand-300 ring-4 ring-sand-300/25 shadow-lg"
        style={{ left: `${x}%`, top: `${y}%` }}
        title={`Alignement ${reputation.loyalty >= 0 ? '+' : ''}${reputation.loyalty} · Doctrine ${reputation.method >= 0 ? '+' : ''}${reputation.method}`}
      />
    </div>
  )
}
